import React, { useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { PlusOutlined } from '@ant-design/icons';
import {
  IInbound,
  IInboundCount,
  IInboundItem,
  ISearchInboundReq,
  ISearchInboundRes,
} from '@/interfaces/inbound';
import {
  Button,
  DatePicker,
  Input,
  notification,
  TablePaginationConfig,
} from 'antd';
import { InboundMenu } from '@/configs/menuItems';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  calculateCost,
  countInbounds,
  getInboundInvoice,
  searchInboundItemsError,
  searchInbounds,
  sendInboundInvoice,
  syncDelievry,
} from '@/adapters/inbound';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import InboundTable from './components/inbound-table';
import locale from 'antd/es/date-picker/locale/th_TH';
import { Dayjs } from 'dayjs';
import { AxiosResponse } from 'axios';
import SubMenu from '@/components/menu/sub-menu';
import { getPathSegment } from '@/utils/pathUtils';
import InboundTabs from './components/inbound-tabs';
import { EInboundStatus, EInboundType } from '@/enums/EInbound';
import InboundItemTable from './components/inbound-item-table';
import { isSuperAdmin } from '@/utils/permissionUtils';
import { useNavigate } from 'react-router-dom';

const { Search } = Input;

const _criteria: ISearchInboundReq = {
  pageSize: 10,
  pageStart: 1,
  status: EInboundStatus.OPEN,
  type: EInboundType.NORMAL,
};

const Inbound = () => {
  const navigate = useNavigate();
  const pathSegment = getPathSegment();
  const isSuperAdminRole = isSuperAdmin();
  const [criteria, setCriteria] = useState<ISearchInboundReq>({
    ..._criteria,
  });

  const { data, refetch, isLoading, isFetching } = useQuery<
    AxiosResponse<ISearchInboundRes>,
    Error
  >({
    queryKey: ['search-inbound', criteria],
    queryFn: async () => await searchInbounds(criteria),
    enabled: criteria?.status !== EInboundStatus.ERROR,
  });

  const { data: countData, refetch: countRefrech } = useQuery<
    AxiosResponse<IInboundCount>,
    Error
  >({
    queryKey: ['count-inbound'],
    queryFn: async () => await countInbounds(EInboundType.NORMAL),
  });

  const { data: errorItems, refetch: errRefresh } = useQuery<
    AxiosResponse<IInboundItem[]>,
    Error
  >({
    queryKey: ['search-inbound-error-item', criteria],
    queryFn: async () => await searchInboundItemsError(criteria),
    enabled: criteria?.status == EInboundStatus.ERROR,
  });

  const onActionInbound = () => {
    refetch();
    countRefrech();
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IInbound> | SorterResult<IInbound>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IInbound) || newCriteria.orderBy;
      newCriteria.orderType = sorter.order || newCriteria.orderType;
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };

  const mutations = useMutation({
    mutationFn: async () => await getInboundInvoice(),
    mutationKey: ['get-inbound-invoice'],
    onSuccess: data => {
      if (data.status === 200) {
        notification.success({
          message: 'get inbound success.',
        });
        countRefrech();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const sendMutations = useMutation({
    mutationFn: async () => await sendInboundInvoice(),
    mutationKey: ['send-inbound-invoice'],
    onSuccess: data => {
      if (data.status === 200) {
        notification.success({
          message: 'sent inbound success.',
        });
        countRefrech();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const calculateCostMutations = useMutation({
    mutationFn: async () => await calculateCost(),
    mutationKey: ['calculate-cost'],
    onSuccess: data => {
      if (data.status === 200) {
        notification.success({
          message: 'cal cost success.',
        });
        countRefrech();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const syncDeliverys = useMutation({
    mutationFn: async () => await syncDelievry(),
    mutationKey: ['calculate-cost'],
    onSuccess: data => {
      if (data.status === 200) {
        notification.success({
          message: 'sync delivery success',
        });
        countRefrech();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  return (
    <>
      <SubMenu active={pathSegment} items={InboundMenu} />
      <Card>
        <Title title="Inbound">
          <div className="flex flex-row gap-2">
            {isSuperAdminRole && (
              <>
                <Button
                  type="primary"
                  ghost
                  loading={syncDeliverys.isPending}
                  onClick={async () => syncDeliverys.mutateAsync()}
                >
                  SYNC DELIVERY
                </Button>
                <Button
                  type="primary"
                  ghost
                  loading={mutations.isPending}
                  onClick={async () => mutations.mutateAsync()}
                >
                  GET INV
                </Button>
                <Button
                  type="primary"
                  ghost
                  loading={sendMutations.isPending}
                  onClick={async () => sendMutations.mutateAsync()}
                >
                  SEND INV
                </Button>
                <Button
                  type="primary"
                  ghost
                  loading={calculateCostMutations.isPending}
                  onClick={async () => calculateCostMutations.mutateAsync()}
                >
                  CAL COST
                </Button>
              </>
            )}
            <Button type="primary" onClick={() => navigate(`/inbound/create`)}>
              <PlusOutlined /> เพิ่มใหม่
            </Button>
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Only this part should scroll horizontally */}
            <div className="overflow-x-auto max-w-full">
              <div className="min-w-max">
                <InboundTabs
                  data={countData?.data}
                  status={criteria?.status}
                  onChange={status =>
                    setCriteria({
                      ...criteria,
                      status,
                      pageStart: 1,
                      pageSize: 10,
                    })
                  }
                />
              </div>
            </div>

            {/* Other filters */}
            <DatePicker.RangePicker
              size="large"
              locale={locale}
              onChange={onRangeChange}
            />
            <Search
              className="md:max-w-[400px] md:min-w-[400px]"
              placeholder="ค้นหา"
              allowClear
              size="large"
              enterButton="ค้นหา"
              onSearch={onSearch}
            />
          </div>
          {criteria?.status !== EInboundStatus.ERROR ? (
            <InboundTable
              criteria={criteria}
              inbound={data?.data || { data: [], totalRecords: 0 }}
              loading={isLoading || isFetching}
              onEditData={item => console.log(item)}
              onDelete={onActionInbound}
              onTableChange={onTableChange}
            />
          ) : (
            <InboundItemTable
              loading={false}
              items={errorItems?.data || []}
              onOk={() => {
                errRefresh();
                countRefrech();
              }}
            />
          )}
        </div>
      </Card>
    </>
  );
};

export default Inbound;
